import { Fragment, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { CheckRounded, ErrorOutlineRounded, InfoOutlined } from '@mui/icons-material';
import PropTypes from 'prop-types';

export default function InfoModal({ data, onClose }) {
    const [open, setOpen] = useState(false);

    const {
        isOpen,
        title,
        message,
        content,
        type = 'info',
        icon = null,
        actionLabel = 'CLOSE',
        showDialogActions = true,
        preventClose = false
    } = data || {};

    const modalTheme = {
        error: {
            primary: '#f78181',
            hover: '#e85252',
            light: '#fff1f1',
            lighter: '#fff8f8'
        },
        info: {
            primary: '#2da8de',
            hover: '#2498ca',
            light: '#eaf7fd',
            lighter: '#f7fcff'
        },
        success: {
            primary: '#2ca58d',
            hover: '#27947f',
            light: '#e7f7f3',
            lighter: '#f5fbf9'
        }
    };

    const currentTheme = modalTheme[type] || modalTheme.info;

    useEffect(() => {
        setOpen(Boolean(isOpen));
    }, [isOpen]);

    const handleClose = (event, reason) => {
        if (preventClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            return;
        }
        setOpen(false);
        onClose?.();
    };

    const renderIcon = () => {
        if (icon) {
            return icon;
        }

        const commonStyles = {
            fontSize: '4rem',
            color: currentTheme.primary
        };

        if (type === 'error') {
            return <ErrorOutlineRounded sx={commonStyles} />;
        }

        if (type === 'success') {
            return <CheckRounded sx={commonStyles} />;
        }

        return <InfoOutlined sx={commonStyles} />;
    };

    return (
        <Fragment>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="info-modal-title"
                aria-describedby="info-modal-description"
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: 520,
                        m: 2,
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
                        backgroundImage: 'none'
                    }
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            bgcolor: 'rgba(15, 23, 42, 0.58)',
                            backdropFilter: 'blur(2px)'
                        }
                    }
                }}
            >
                <DialogContent
                    sx={{
                        position: 'relative',
                        px: { xs: 3, sm: 5 },
                        pt: { xs: 4, sm: 5 },
                        pb: 2,
                        textAlign: 'center',
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        <Box
                            sx={{
                                width: 112,
                                height: 112,
                                mx: 'auto',
                                mb: 3,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: currentTheme.light,
                                boxShadow: `0 0 0 14px ${currentTheme.lighter}`
                            }}
                        >
                            {renderIcon()}
                        </Box>

                        {title && (
                            <Typography
                                id="info-modal-title"
                                component="h2"
                                sx={{
                                    fontSize: {
                                        xs: '1.55rem',
                                        sm: '1.85rem'
                                    },
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    color: 'text.primary'
                                }}
                            >
                                {title}
                            </Typography>
                        )}

                        <Box
                            sx={{
                                width: 48,
                                height: 3,
                                borderRadius: 999,
                                bgcolor: currentTheme.primary,
                                mx: 'auto',
                                my: 2
                            }}
                        />

                        {message && (
                            <Typography
                                id="info-modal-description"
                                sx={{
                                    maxWidth: 380,
                                    mx: 'auto',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.65,
                                    color: 'text.secondary'
                                }}
                            >
                                {message}
                            </Typography>
                        )}

                        {content && (
                            <Box
                                sx={{
                                    mt: 2.5,
                                    textAlign: 'left'
                                }}
                            >
                                {content}
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                {showDialogActions && (
                    <DialogActions
                        sx={{
                            position: 'relative',
                            px: { xs: 3, sm: 5 },
                            pt: 2,
                            pb: { xs: 3, sm: 4 },
                            display: 'block',
                            textAlign: 'center'
                        }}
                    >
                        <Button
                            onClick={handleClose}
                            autoFocus
                            variant="contained"
                            size="large"
                            disableElevation
                            sx={{
                                width: { xs: '100%', md: '75%' },
                                minHeight: 48,
                                borderRadius: 2,
                                bgcolor: currentTheme.primary,
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                textTransform: 'none',
                                boxShadow: `0 8px 20px ${currentTheme.primary}33`,
                                '&:hover': {
                                    bgcolor: currentTheme.hover,
                                    boxShadow: `0 10px 24px ${currentTheme.primary}45`
                                }
                            }}
                        >
                            {actionLabel}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        </Fragment>
    );
}

InfoModal.propTypes = {
    data: PropTypes.shape({
        isOpen: PropTypes.bool,
        title: PropTypes.string,
        message: PropTypes.string,
        content: PropTypes.node,
        type: PropTypes.oneOf(['error', 'info', 'success']),
        icon: PropTypes.node,
        actionLabel: PropTypes.string
    }),
    onClose: PropTypes.func
};

InfoModal.defaultProps = {
    data: {},
    onClose: () => {}
};
